"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Boxes, ChevronDown, Heart, House, Menu, Search, ShoppingBag, X } from "lucide-react";
import type { Category } from "@/lib/types";

const links = [
  { href: "/", label: "خانه", icon: House },
  { href: "/products", label: "محصولات", icon: Boxes },
];

export function MobileBottomNav({ categories = [] }: { categories?: Category[] }) {
  const pathname = usePathname();
  const [openPanel, setOpenPanel] = useState<{ type: "search" | "menu"; pathname: string } | null>(null);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const panel = openPanel?.pathname === pathname ? openPanel.type : null;
  const searchRef = useRef<HTMLInputElement>(null);
  const hidden = pathname === "/cart" || pathname === "/checkout" || pathname.startsWith("/products/");
  const togglePanel = (type: "search" | "menu") => {
    setOpenPanel((current) => current?.pathname === pathname && current.type === type ? null : { type, pathname });
  };

  useEffect(() => {
    if (panel === "search") searchRef.current?.focus();
  }, [panel]);

  if (hidden) return null;

  const active = (href: string) => href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <nav
      className="mobile-bottom-nav"
      aria-label="ناوبری سریع"
    >
      {panel === "search" && (
        <form className="mobile-bottom-panel mobile-bottom-search" action="/products" role="search">
          <label className="sr-only" htmlFor="mobile-bottom-search-input">جست‌وجوی محصولات</label>
          <input ref={searchRef} id="mobile-bottom-search-input" name="q" type="search" placeholder="نام محصول را بنویسید" />
          <button type="submit" aria-label="جست‌وجو"><Search size={19} aria-hidden="true" /></button>
        </form>
      )}
      {panel === "menu" && (
        <div className="mobile-bottom-panel mobile-bottom-menu">
          <Link href="/products"><Boxes size={17} aria-hidden="true" /> همهٔ محصولات</Link>
          <Link href="/#featured">منتخب‌ها</Link>
          <Link href="/offers">شگفت‌انگیزها</Link>
          <button type="button" className="mobile-menu-category-toggle" aria-expanded={categoriesExpanded} onClick={() => setCategoriesExpanded((expanded) => !expanded)}>
            دسته‌بندی‌ها <ChevronDown size={16} aria-hidden="true" />
          </button>
          {categoriesExpanded && <div className="mobile-menu-categories">
            <Link href="/categories">همهٔ دسته‌بندی‌ها</Link>
            {categories.filter((category) => category.slug !== "uncategorized").map((category) => (
              <Link key={category.slug} href={`/categories/${category.slug}`}>{category.name}</Link>
            ))}
          </div>}
          <Link href="/#process">داستان ساخت</Link>
          <Link href="/info/about">دربارهٔ ما</Link>
          <Link href="/info/faq">پرسش‌های متداول</Link>
          <Link href="/support">تماس با ما و پشتیبانی</Link>
          <Link href="/cart"><ShoppingBag size={17} aria-hidden="true" /> سبد خرید</Link>
        </div>
      )}

      <div className="mobile-bottom-items">
        {links.map(({ href, label, icon: Icon }) => (
          <Link
            key={href}
            href={href}
            aria-label={label}
            aria-current={active(href) ? "page" : undefined}
            className={active(href) ? "is-active" : undefined}
          >
            <Icon size={20} strokeWidth={active(href) ? 2.3 : 1.8} aria-hidden="true" />
            <span>{label}</span>
          </Link>
        ))}
        <button
          type="button"
          aria-label={panel === "search" ? "بستن جست‌وجو" : "جست‌وجو"}
          aria-expanded={panel === "search"}
          className={panel === "search" ? "is-active" : undefined}
          onClick={() => togglePanel("search")}
        >
          {panel === "search" ? <X size={20} aria-hidden="true" /> : <Search size={20} aria-hidden="true" />}
          <span>جست‌وجو</span>
        </button>
        <Link
          href="/account?section=wishlist"
          aria-label="علاقه‌مندی‌ها"
          aria-current={pathname === "/account" ? "page" : undefined}
          className={pathname === "/account" ? "is-active" : undefined}
        >
          <Heart size={20} strokeWidth={1.8} aria-hidden="true" />
          <span>علاقه‌مندی</span>
        </Link>
        <button
          type="button"
          aria-label={panel === "menu" ? "بستن منو" : "منو"}
          aria-expanded={panel === "menu"}
          className={panel === "menu" ? "is-active" : undefined}
          onClick={() => togglePanel("menu")}
        >
          {panel === "menu" ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          <span>منو</span>
        </button>
      </div>
    </nav>
  );
}
